/**
 * The reminder sound, synthesised rather than shipped.
 *
 * A file would mean either an external asset — which the CSP blocks, quite
 * deliberately — or tens of kilobytes of base64 in the bundle for two notes.
 * Two oscillators cost nothing and work offline.
 *
 * Browsers refuse to start audio before the page has been interacted with, so
 * the context is created once and resumed on the first gesture. A reminder
 * firing on a page nobody has touched yet is silent; the dialog still appears,
 * which is the part that matters.
 */

let context: AudioContext | null = null
let unlocked = false

function audio(): AudioContext | null {
  if (typeof window === 'undefined') return null
  const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  if (!context) {
    try {
      context = new Ctor()
    } catch {
      return null
    }
  }
  return context
}

/**
 * Resume the audio context on the first user gesture.
 *
 * Called once at startup. Without it the first reminder of a session is often
 * silent: the context is created suspended and only a gesture may resume it.
 */
export function unlockChime(): void {
  if (unlocked || typeof document === 'undefined') return
  unlocked = true
  const resume = () => {
    void audio()?.resume().catch(() => {})
  }
  // `once` on each: any one gesture is enough, and we do not want to hold
  // listeners for the life of the page.
  document.addEventListener('pointerdown', resume, { once: true })
  document.addEventListener('keydown', resume, { once: true })
}

/** Two rising notes — noticeable without being an alarm. */
export function playChime(): void {
  const ctx = audio()
  if (!ctx) return
  void ctx.resume().catch(() => {})
  if (ctx.state !== 'running') return

  const now = ctx.currentTime
  // G5 then C6: a rising fourth reads as "attention" rather than "error".
  for (const [index, frequency] of [784, 1047].entries()) {
    const at = now + index * 0.18
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.value = frequency
    // An envelope, not a square edge: an abrupt start and stop on a sine wave
    // produces an audible click at both ends.
    gain.gain.setValueAtTime(0, at)
    gain.gain.linearRampToValueAtTime(0.18, at + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.35)
    osc.connect(gain).connect(ctx.destination)
    osc.start(at)
    osc.stop(at + 0.4)
  }
}
